/**
 * iter839 — Inter-Branch Due dashboard widget.
 *
 * Shows the total outstanding inter-branch balance for the current
 * month plus the top 3 owing pairs. Click-through opens the full
 * Inter-Branch report with the same period pre-selected. Also renders
 * a compact "Close Month" button (admin/owner only, checked by the
 * backend) that snapshots the month and carries the balance forward.
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeftRight, ChevronRight, Loader2, Lock, History } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/currency';

const money = (v) => formatCurrency(v || 0);

export default function InterBranchDueWidget() {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, hisRes] = await Promise.all([
        api.get('/inter-branch-due-summary'),
        api.get('/inter-branch/close-history?limit=6').catch(() => ({ data: { history: [] } })),
      ]);
      setData(sumRes.data);
      setHistory(hisRes.data?.history || []);
    } catch {
      setData(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const closeMonth = async () => {
    if (!data?.period) return;
    if (!window.confirm(`Close inter-branch balances for ${data.period}? Outstanding due will carry forward to the next month as an opening balance.`)) return;
    setClosing(true);
    try {
      const res = await api.post('/inter-branch/close-month', { period: data.period });
      if (res.data?.already_closed) {
        toast.info(`${data.period} was already closed`);
      } else {
        const carried = res.data?.close?.pairs_carried?.length || 0;
        toast.success(`Closed ${data.period} — ${carried} pair${carried === 1 ? '' : 's'} carried to next month`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to close month');
    } finally { setClosing(false); }
  };

  return (
    <Card className="hover:shadow-md transition-shadow" data-testid="inter-branch-due-widget">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 flex items-center justify-center">
              <ArrowLeftRight size={16} />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">Inter-Branch Due</div>
              <div className="text-[10px] text-muted-foreground/70">{data?.period || 'Current month'}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate(data?.deep_link || '/accounting/inter-branch')}
            className="text-[11px] text-indigo-600 hover:text-indigo-800 flex items-center gap-0.5 font-medium"
            data-testid="inter-branch-due-drilldown"
            title="Open full Inter-Branch Ledger report"
          >
            View all <ChevronRight size={12} />
          </button>
        </div>

        {loading ? (
          <div className="py-6 flex items-center justify-center text-muted-foreground text-sm">
            <Loader2 size={16} className="animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <>
            <div className="text-2xl font-bold tabular-nums text-indigo-900 dark:text-indigo-100" data-testid="inter-branch-due-total">
              {money(data?.total_due)}
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              {data?.pairs_count || 0} outstanding pair{data?.pairs_count === 1 ? '' : 's'}
              {data?.settlements_total > 0 ? ` · ${money(data.settlements_total)} settled this month` : ''}
            </div>
            {data?.by_mode && ((data.by_mode.cash || 0) !== 0 || (data.by_mode.bank || 0) !== 0 || (data.by_mode.other || 0) !== 0) && (
              <div className="text-[10px] text-muted-foreground mt-1 flex flex-wrap gap-x-3" data-testid="inter-branch-due-mode-split">
                <span>Cash: <b className="text-emerald-600">{money(data.by_mode.cash)}</b></span>
                <span>Bank: <b className="text-sky-600">{money(data.by_mode.bank)}</b></span>
                {(data.by_mode.other || 0) > 0 && <span>Other: <b>{money(data.by_mode.other)}</b></span>}
              </div>
            )}

            {Array.isArray(data?.top_pairs) && data.top_pairs.length > 0 && (
              <ul className="mt-3 space-y-1 text-xs" data-testid="inter-branch-due-top-pairs">
                {data.top_pairs.map((p, i) => (
                  <li key={`${p.from_id}-${p.to_id}`} className="flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full bg-indigo-500 shrink-0" />
                    <span className="truncate">
                      <span className="font-medium">{p.from_branch}</span>
                      <span className="text-muted-foreground"> owes </span>
                      <span className="font-medium">{p.to_branch}</span>
                    </span>
                    <span className="ml-auto tabular-nums font-semibold" data-testid={`inter-branch-due-pair-${i}`}>
                      {money(p.owes)}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {data?.pairs_count > 0 && (
              <div className="mt-3 pt-3 border-t border-border">
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full h-8 text-[11px] border-indigo-300 text-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-950/30"
                  onClick={closeMonth}
                  disabled={closing}
                  data-testid="inter-branch-close-month"
                  title="Snapshot the current outstanding due and carry it forward as an opening balance for next month"
                >
                  {closing ? <><Loader2 size={12} className="mr-1.5 animate-spin" /> Closing…</> : <><Lock size={11} className="mr-1.5" /> Close month & carry forward</>}
                </Button>
              </div>
            )}

            {/* iter840 — Auto-close history. Shows the last few closed
                months. Auto-close runs every 6h on the server so users
                can audit what the scheduler did. */}
            {history.length > 0 && (
              <div className="mt-2 pt-2 border-t border-border">
                <button
                  type="button"
                  onClick={() => setShowHistory((v) => !v)}
                  className="w-full flex items-center justify-between text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="inter-branch-history-toggle"
                >
                  <span className="flex items-center gap-1">
                    <History size={10} />
                    {history.length} closed month{history.length === 1 ? '' : 's'} on record · auto-close every 6h
                  </span>
                  <ChevronRight size={10} className={`transition-transform ${showHistory ? 'rotate-90' : ''}`} />
                </button>
                {showHistory && (
                  <ul className="mt-1.5 space-y-0.5 text-[10px]" data-testid="inter-branch-history-list">
                    {history.map((h) => (
                      <li key={h.id} className="flex items-center gap-2">
                        <span className="w-1 h-1 rounded-full bg-slate-400 shrink-0" />
                        <span className="font-medium">{h.period}</span>
                        <span className="text-muted-foreground">{h.pairs_count} pair{h.pairs_count === 1 ? '' : 's'}</span>
                        <span className="ml-auto tabular-nums text-muted-foreground">
                          {money(h.total_due_at_close)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
